import 'abortcontroller-polyfill/dist/abortcontroller-polyfill-only'

import { createRoot } from 'react-dom/client'
import { CommunicationService } from 'entities/CommunicationService'
import { setupRootStyle, startRootStyleWatchers } from 'utils/rootStyle'
import createOpenChatButton from 'utils/createOpenButton'
import createInvisibleInput from 'utils/createInvisibleInput'
import {
  mobileBreakpoint,
  rootId,
  UrlHashesFragments,
  UrlGetParams,
  WidgetModes,
} from 'consts'
import TRACKING_INFORMATION from 'utils/trackingInformation'
import shouldChatWidgetOpen from 'utils/shouldChatWidgetOpen'
import {
  clearSessionStorage,
  getBackendSession,
  setBackendSession,
} from 'utils/sessionStorage'
import { AvailableCampaigns } from 'entities/Config'
import getTheme from 'utils/getTheme'
import fetchUrl from 'utils/fetchUrl'
import Abort from 'utils/abort'
import getAdServerEventsUrls from 'utils/getAdServerEventsUrls'
import { getTenant, getLanguage } from 'utils/getTenantSpecificValues'
import dataLayerPushEvent, { DataLayerEvents } from 'utils/dataLayerPushEvent'
import type { EventsUrls } from 'utils/getAdServerEventsUrls'
import type { ThemeConfig, ThemesListConfig } from 'entities/Config'
import clientConfigURL from 'clientConfig/clientConfig.json'

export interface RootElement extends HTMLElement {
  setCssProperties: (theme: ThemeConfig) => void
}

const init = () => {
  const container = document.getElementById(rootId) as RootElement

  const urlHash = window.location.hash
  const getParams = Array.from(
    new URLSearchParams(window.location.search).keys()
  )

  const withCounter =
    window.innerWidth <= mobileBreakpoint &&
    (urlHash.indexOf(UrlHashesFragments.SupportChatNotifications) !== -1 ||
      getParams.includes(UrlGetParams.SupportChatNotifications))

  container.setCssProperties = (theme: ThemeConfig) => {
    const { cssProperties, palette, backend } = theme
    const currentBackend = getBackendSession()

    if (currentBackend !== backend) {
      clearSessionStorage()
    }

    setBackendSession(backend || CommunicationService.A)

    const cssProps = {
      ...palette,
      ...cssProperties,
    }

    Object.entries(cssProps).forEach(([prop, value]) => {
      container.style.setProperty(`--${prop}`, value)
    })
  }

  const matchPaths = (paths: Array<string>) => {
    const pathName = window.location.pathname
    const isMatching: boolean = paths.some((path: string) =>
      pathName.startsWith(path)
    )

    return isMatching
  }

  const initialiseConfig = async (): Promise<{
    showWidget: boolean
    themeConfig: ThemeConfig
    availableConfigs: ThemesListConfig
  }> => {
    const data = await (await fetch(`${clientConfigURL}`)).json()
    const { global, default: defaultConfig, ...tenantConfigs } = data
    const tenant = getTenant()
    const language = getLanguage()
    const themeConfigs = {
      [AvailableCampaigns.default]: defaultConfig,
      ...tenantConfigs?.[tenant]?.[language],
    }
    const themeConfig = getTheme(themeConfigs as ThemesListConfig)

    container.setCssProperties(themeConfig)

    const globalTenantLanguageConfig = global?.[tenant]?.[language]
    let showWidget = false

    if (globalTenantLanguageConfig) {
      const { hiddenPages = [], chatConsultOnlyPages = [] } =
        globalTenantLanguageConfig

      showWidget = globalTenantLanguageConfig.showWidget

      if (showWidget && hiddenPages.length) {
        const isHiddenPageMatched = matchPaths(hiddenPages)

        if (isHiddenPageMatched) {
          showWidget = false
        }
      }

      const isChatConsultOnlyPagesMatched = matchPaths(chatConsultOnlyPages)
      if (
        isChatConsultOnlyPagesMatched &&
        showWidget &&
        chatConsultOnlyPages.length
      ) {
        showWidget =
          isChatConsultOnlyPagesMatched &&
          themeConfig.id !== AvailableCampaigns.default
      }
    }

    return {
      showWidget,
      themeConfig,
      availableConfigs: themeConfigs,
    }
  }

  // iOS will not deploy virtual keyboard if we focus an input outside of
  // user ineracion event. But it does not hide the keyboard when keyboard
  // is already out and we jump between inputs.
  // We create this (invisible) input so that keyboard gets shown and
  // later when message input is rendered, we switch to that.
  const input = createInvisibleInput()

  const chatMode = container.dataset?.chatmode as WidgetModes
  let widgetMode = WidgetModes.Default

  if (chatMode && Object.values(WidgetModes).includes(chatMode)) {
    widgetMode = chatMode
  }

  const loadAnalyticsTracker = async () => {
    /* webpackMode: "lazy" */

    const analytics = await import(
      /* webpackChunkName: "analytics" */ './utils/analytics'
    )

    return analytics.default
  }

  const loadCheckUserlikeState = async () => {
    /* webpackMode: "lazy" */

    const { default: checkUserlikeState } = await import(
      /* webpackChunkName: "checkUserlikeState" */ './utils/userlike'
    )

    return checkUserlikeState
  }

  const chatConsultInitialAnalytics = async (campaign: AvailableCampaigns) => {
    const customAnalytics = await loadAnalyticsTracker()
    customAnalytics.track('consult-offered', {
      category: 'chat-consult',
      label: campaign,
    })
  }

  const loadAndOpenApplication = async (
    themeConfig: ThemeConfig,
    availableConfigs: ThemesListConfig,
    clickUrl?: string
  ) => {
    input.disabled = true

    const customAnalytics = await loadAnalyticsTracker()
    customAnalytics.track(TRACKING_INFORMATION.widgetOpenedByClick.action, {
      category: TRACKING_INFORMATION.widgetOpenedByClick.category,
      label: TRACKING_INFORMATION.widgetOpenedByClick.label,
    })

    dataLayerPushEvent(DataLayerEvents.ChatBotOpened)

    const checkUserlikeState = await loadCheckUserlikeState()
    checkUserlikeState()
    const root = createRoot(container)
    /**
     * Uncomment next line and change `disabled` to `false` if you need initial focus on input fields
     * for phones
     */
    // input.focus()

    /* webpackMode: "lazy" */
    ;(await import('./init')).default({
      root,
      container,
      themeConfig,
      availableConfigs,
      invisibleDummyInput: input,
      mode: widgetMode,
      clickUrl,
    })

    setTimeout(() => {
      input.disabled = true
    }, 50)
  }

  const loadMobileApp = async (
    themeConfig: ThemeConfig,
    availableConfigs: ThemesListConfig
  ) => {
    loadAndOpenApplication(themeConfig, availableConfigs)
    setupRootStyle(container)
    startRootStyleWatchers(container)
    const checkUserlikeState = await loadCheckUserlikeState()
    checkUserlikeState()
  }

  const loadWebApp = async (
    themeConfig: ThemeConfig,
    availableConfigs: ThemesListConfig
  ) => {
    const button = createOpenChatButton(themeConfig, withCounter)
    const chatWindowOpensOnLoad = shouldChatWidgetOpen()
    const { clickUrl, impressionUrl, viewUrl, placeUrl }: EventsUrls =
      await getAdServerEventsUrls(themeConfig.adServerProperties)

    if (placeUrl) {
      void fetchUrl(placeUrl)
    }

    button.addEventListener('click', () => {
      button.disabled = true

      if (viewUrl) {
        void fetchUrl(viewUrl)
      }

      if (impressionUrl) {
        void fetchUrl(impressionUrl)
      }

      loadAndOpenApplication(themeConfig, availableConfigs, clickUrl)
    })

    if (chatWindowOpensOnLoad) {
      const customAnalytics = await loadAnalyticsTracker()
      customAnalytics.track(TRACKING_INFORMATION.widgetOpenedByHash.action, {
        category: TRACKING_INFORMATION.widgetOpenedByHash.category,
        label: TRACKING_INFORMATION.widgetOpenedByHash.label,
      })
    }

    if (chatWindowOpensOnLoad && !withCounter) {
      loadAndOpenApplication(themeConfig, availableConfigs, clickUrl)
    }

    setupRootStyle(container)
    startRootStyleWatchers(container)

    if (
      themeConfig.id !== AvailableCampaigns.default &&
      !getParams.includes(UrlGetParams.ViewProduct)
    ) {
      chatConsultInitialAnalytics(themeConfig.id)
    }

    container.appendChild(button)
  }

  const initialiseApp = async () => {
    const { showWidget, themeConfig, availableConfigs } =
      await initialiseConfig()

    if (!showWidget) {
      return
    }

    switch (widgetMode) {
      case WidgetModes.MobileApp:
        loadMobileApp(themeConfig, availableConfigs)
        break

      default:
        loadWebApp(themeConfig, availableConfigs)
    }
  }

  initialiseApp()
}

init()

const dispatchLocationChangeEvent = () => {
  if (!window.history) {
    return
  }

  const {
    history: { pushState, replaceState },
  } = window

  window.history.pushState = (...args) => {
    pushState.apply(window.history, args)
    window.dispatchEvent(new Event('pushstate'))
    window.dispatchEvent(new Event('locationchange'))
  }

  window.history.replaceState = (...args) => {
    replaceState.apply(window.history, args)
    window.dispatchEvent(new Event('replacestate'))
    window.dispatchEvent(new Event('locationchange'))
  }

  window.addEventListener('popstate', () => {
    window.dispatchEvent(new Event('locationchange'))
  })
}

const reloadApp = () => {
  const container = document.getElementById(rootId) as RootElement

  container.remove()

  const rootElement = document.createElement('div')
  rootElement.setAttribute('id', rootId)

  document.body.appendChild(rootElement)

  init()
}

dispatchLocationChangeEvent()

window.addEventListener('locationchange', () => {
  reloadApp()
})

window.addEventListener('beforeunload', () => {
  Abort.getInstance().abort()
})
